'use client';

import { useEffect, useState } from 'react';

// Singleton global state for the synced timer
let subscribers: Set<(time: number) => void> = new Set();
let timeoutHandle: ReturnType<typeof setTimeout> | null = null;

/**
 * Schedules the next tick to fire exactly at the next whole-second boundary
 * of the wall clock (Date.now() % 1000 === 0). This prevents the cumulative
 * drift that setInterval causes, keeping all clients in sync with server
 * timestamps like auctionScheduledEndAt.
 */
function scheduleNextTick() {
  const msUntilNextSecond = 1000 - (Date.now() % 1000);

  timeoutHandle = setTimeout(() => {
    const currentNow = Date.now();
    subscribers.forEach((cb) => cb(currentNow)); // Keep ticking only while there are active subscribers

    if (subscribers.size > 0) {
      scheduleNextTick();
    } else {
      timeoutHandle = null;
    }
  }, msUntilNextSecond);
}

function startGlobalTimer() {
  if (timeoutHandle === null && subscribers.size > 0) {
    scheduleNextTick();
  }
}

function stopGlobalTimer() {
  if (timeoutHandle !== null && subscribers.size === 0) {
    clearTimeout(timeoutHandle);
    timeoutHandle = null;
  }
}

/**
 * When the tab/screen is hidden (e.g. laptop lid closed, screen locked,
 * tab switched), browsers suspend or heavily throttle setTimeout callbacks.
 * On return, the timer chain may be stalled. This listener detects when the
 * page becomes visible again, immediately pushes a fresh timestamp to all
 * subscribers, and restarts the tick chain so the countdown resumes without
 * requiring a manual page refresh.
 */
function handleVisibilityChange() {
  if (document.visibilityState === 'visible' && subscribers.size > 0) {
    // Push fresh time immediately so the UI updates right away
    const currentNow = Date.now();
    subscribers.forEach((cb) => cb(currentNow));

    // Restart the tick chain (clear any stale pending timeout first)
    if (timeoutHandle !== null) {
      clearTimeout(timeoutHandle);
      timeoutHandle = null;
    }
    scheduleNextTick();
  }
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', handleVisibilityChange);
}

/**
 * Restarts the global timer and immediately pushes a fresh timestamp to all
 * subscribers. Call this from platform-specific "app resumed" handlers
 * (e.g. React Native AppState) where the browser visibilitychange API is
 * not available.
 */
export function restartSyncedTimer() {
  if (subscribers.size === 0) return;

  const currentNow = Date.now();
  subscribers.forEach((cb) => cb(currentNow));

  if (timeoutHandle !== null) {
    clearTimeout(timeoutHandle);
    timeoutHandle = null;
  }
  scheduleNextTick();
}

/**
 * A highly optimized global timer that ensures all consuming components
 * render with perfectly synchronized timing without visual drift.
 *
 * Uses a self-correcting setTimeout that snaps each tick to the next
 * wall-clock second boundary, so all clients sharing the same
 * auctionScheduledEndAt timestamp will display the same countdown value.
 *
 * @returns The current `Date.now()` timestamp, updated every 1s aligned to wall-clock seconds.
 */
export function useSyncedTimer() {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    // Add this component instance to the subscribers loop
    subscribers.add(setNow); // If this is the first subscriber, boot up the global timer

    startGlobalTimer();

    return () => {
      // Remove this component instance from the subscribers loop on unmount
      subscribers.delete(setNow); // If this was the last subscriber, tear down the global timer to save memory

      stopGlobalTimer();
    };
  }, []);

  return now;
}
